import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type UsePaginationLabelReturn = {
    currentPage: number;
    totalPages: number;
    handlePrevious: () => void;
    handleNext: () => void;
    handleReset: () => void;
    handleCurrent: (page: number) => void;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageRange: readonly [number, number, number] | null;
    setTotalResults: (totalResults: number | null) => void;
    pageSize: number;
    totalResults: number | null;
};

const useIsomorphicPageState = (defaultPage: number, useQueryParams: boolean) => {
    const [queryParams, setQueryParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(
        useQueryParams && queryParams.has('page') && !Number.isNaN(parseInt(queryParams.get('page') as string, 10))
            ? parseInt(queryParams.get('page') as string, 10)
            : defaultPage
    );

    useEffect(() => {
        if (!useQueryParams) return;

        setQueryParams((prev) => {
            prev.set('page', currentPage.toString());
            return prev;
        }, {
            replace: true,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, useQueryParams]);

    return [currentPage, setCurrentPage] as const;
};

export const usePaginationLabel = ({
    defaultPage = 1,
    totalResults: defaultTotalResults = null,
    pageSize,
    queryParamsEnabled = false,
}: {
    defaultPage?: number;
    totalResults?: number | null;
    pageSize: number;
    queryParamsEnabled?: boolean;
}): UsePaginationLabelReturn => {
    const [totalResults, setTotalResults] = useState<number | null>(defaultTotalResults);
    const [currentPage, setCurrentPage] = useIsomorphicPageState(defaultPage, queryParamsEnabled);

    const totalPages = useMemo(() => {
        if (totalResults === null) return 1;

        return Math.ceil(totalResults / pageSize);
    }, [totalResults, pageSize]);

    const hasNextPage = useMemo(() => {
        if (totalPages === null) return false;
        return currentPage < totalPages;
    }, [currentPage, totalPages]);

    const hasPreviousPage = useMemo(() => currentPage > 1, [currentPage]);

    const pageRange = useMemo(() => {
        if (totalResults === null) return null;

        return [
            (currentPage - 1) * pageSize + 1,
            Math.min(currentPage * pageSize, totalResults),
            totalResults,
        ] as const;
    }, [totalResults, pageSize, currentPage]);

    const handlePrevious = () => {
        if (!hasPreviousPage) return;

        setCurrentPage((cur) => cur - 1);
    };

    const handleNext = () => {
        if (!hasNextPage) return;

        setCurrentPage((cur) => cur + 1);
    };

    const handleCurrent = (page: number) => {
        if (page < 1 || page > totalPages) return;

        setCurrentPage(page);
    };

    const handleReset = () => setCurrentPage(1);

    return {
        currentPage,
        totalPages,
        handlePrevious,
        handleNext,
        handleReset,
        handleCurrent,
        hasNextPage,
        hasPreviousPage,
        pageRange,
        setTotalResults,
        pageSize,
        totalResults,
    };
};
